<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { WorkspaceDTO } from '@/types'

import { useAppToast } from '@/composables/useAppToast'
import { updateWorkspaceIdentity, updateWorkspaceSettings } from '@/services/workspaceService'
import { getApiErrorMessage } from '@/utils/apiError'

definePage({
  meta: {
    title: 'router.pages.workspaces-id-settings-embed',
    requiresAuth: true,
  },
})

const props = defineProps<{
  workspace: WorkspaceDTO
}>()

const emit = defineEmits(['reloadWorkspace'])

const { t } = useI18n()
const toast = useAppToast()
const { hasRole, userInfo } = useUser()
const { adminModeEnabled } = useAdminMode()

const saving = ref(false)
const embedEnabled = ref(false)
const urlSlug = ref('')
const original = ref({
  embedEnabled: false,
  urlSlug: '',
})

const currentUserRole = computed(() => {
  if (!userInfo.value?.id) {
    return null
  }
  const userInWorkspace = props.workspace.users.find((u) => u.userId === userInfo.value!.id)
  return userInWorkspace?.role || null
})

const isWorkspaceAdmin = computed(() => currentUserRole.value === 'admin')
const isSystemAdmin = computed(() => hasRole.value('admin') && adminModeEnabled.value)
const canEdit = computed(() => isWorkspaceAdmin.value || isSystemAdmin.value)
const normalizedSlug = computed(() => normalizeSlug(urlSlug.value))
const hasChanges = computed(
  () =>
    embedEnabled.value !== original.value.embedEnabled ||
    normalizedSlug.value !== original.value.urlSlug,
)
const hostOrigin = computed(() =>
  typeof window === 'undefined' ? 'https://SIGNMEUP_HOST' : window.location.origin,
)
const scriptUrl = computed(() => `${hostOrigin.value}/embed/workspace-offering.js`)
const snippet = computed(
  () => `<div
  data-signmeup-workspace="${escapeAttribute(normalizedSlug.value)}"
  data-theme="auto"
  data-lang="auto"
></div>
\u003cscript async src="${escapeAttribute(scriptUrl.value)}">\u003c/script>`,
)

watch(
  () => props.workspace,
  (workspace) => {
    const resolvedSlug = workspace.urlSlug ?? ''
    embedEnabled.value = workspace.embedEnabled
    urlSlug.value = resolvedSlug
    original.value = {
      embedEnabled: workspace.embedEnabled,
      urlSlug: resolvedSlug,
    }
  },
  { immediate: true },
)

function normalizeSlug(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

function escapeAttribute(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/\u003c/g, '&lt;')
}

async function saveEmbedSettings() {
  if (!canEdit.value) {
    return
  }

  const slug = normalizedSlug.value
  if (!slug) {
    toast.add({
      color: 'warning',
      title: t('pages.workspaces.settings.embed.validationError'),
      description: t('pages.workspaces.settings.embed.slugRequired'),
      duration: 4000,
    })
    return
  }

  try {
    saving.value = true
    let updatedWorkspace: WorkspaceDTO | null = null

    if (slug !== original.value.urlSlug) {
      updatedWorkspace = await updateWorkspaceIdentity(props.workspace.id, {
        nameDE: props.workspace.nameDE,
        urlSlug: slug,
      })
    }

    if (embedEnabled.value !== original.value.embedEnabled) {
      updatedWorkspace = await updateWorkspaceSettings(props.workspace.id, {
        enableFeeManagement: props.workspace.enableFeeManagement,
        feeManagementRole: props.workspace.feeManagementRole,
        contactEmail: props.workspace.contactEmail,
        enableAllocationSummaryEmails: props.workspace.enableAllocationSummaryEmails,
        enableAttendance: props.workspace.enableAttendance,
        enableGrades: props.workspace.enableGrades,
        externalPdfExportIncludeMatriculationId:
          props.workspace.externalPdfExportIncludeMatriculationId,
        externalPdfExportIncludeEmail: props.workspace.externalPdfExportIncludeEmail,
        embedEnabled: embedEnabled.value,
      })
    }

    const savedEmbedEnabled = updatedWorkspace?.embedEnabled ?? embedEnabled.value
    const savedSlug = updatedWorkspace?.urlSlug ?? slug
    original.value = {
      embedEnabled: savedEmbedEnabled,
      urlSlug: savedSlug,
    }
    embedEnabled.value = savedEmbedEnabled
    urlSlug.value = savedSlug
    emit('reloadWorkspace', props.workspace.id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.workspaces.settings.embed.saveSuccess'),
      duration: 3000,
    })
  } catch (error: unknown) {
    const fallbackMessage = t('pages.workspaces.settings.embed.saveError')
    const message =
      (error as { response?: { data?: { errors?: Record<string, string[]>; error?: string } } })
        ?.response?.data?.errors?.UrlSlug?.[0] ??
      (error as { response?: { data?: { errors?: Record<string, string[]>; error?: string } } })
        ?.response?.data?.errors?.urlSlug?.[0] ??
      getApiErrorMessage(error, t, fallbackMessage)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: message,
      duration: 5000,
    })
  } finally {
    saving.value = false
  }
}

async function copySnippet() {
  await navigator.clipboard.writeText(snippet.value)
  toast.add({
    color: 'success',
    title: t('common.messages.success'),
    description: t('pages.workspaces.settings.embed.copySuccess'),
    duration: 3000,
  })
}
</script>

<template>
  <div>
    <div class="mb-6 flex items-center justify-between gap-4">
      <div>
        <h2 class="m-0 text-2xl font-semibold text-neutral-900 dark:text-white">
          {{ t('pages.workspaces.settings.embed.title') }}
        </h2>
        <p class="mt-1 mb-0 text-muted">
          {{ t('pages.workspaces.settings.embed.subtitle') }}
        </p>
      </div>
      <UButton
        icon="i-lucide-save"
        :label="t('common.actions.save')"
        :loading="saving"
        :disabled="!canEdit || !hasChanges"
        @click="saveEmbedSettings"
      />
    </div>

    <AppAlert v-if="!canEdit" color="warning" :closable="false" class="mb-4">
      {{ t('pages.workspaces.settings.embed.permissionWarning') }}
    </AppAlert>

    <div class="mycard mb-3 p-4">
      <div class="settings-toggle">
        <div class="settings-label">
          <label>{{ t('pages.workspaces.settings.embed.enableLabel') }}</label>
          <p>{{ t('pages.workspaces.settings.embed.enableDescription') }}</p>
        </div>
        <USwitch v-model="embedEnabled" :readonly="!canEdit" />
      </div>

      <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div class="flex flex-col gap-2">
          <label for="workspaceEmbedSlug" class="font-medium">
            {{ t('pages.workspaces.settings.embed.slugLabel') }}
          </label>
          <UInput id="workspaceEmbedSlug" v-model="urlSlug" :disabled="!canEdit" />
          <small class="text-muted">
            {{ t('pages.workspaces.settings.embed.slugHelp') }}
          </small>
        </div>

        <div class="flex flex-col gap-2">
          <span class="font-medium">{{ t('pages.workspaces.settings.embed.scriptUrlLabel') }}</span>
          <UInput :model-value="scriptUrl" readonly />
          <small class="text-muted">
            {{ t('pages.workspaces.settings.embed.scriptUrlHelp') }}
          </small>
        </div>
      </div>
    </div>

    <div class="mycard p-4">
      <div class="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 class="m-0 text-xl font-semibold text-neutral-900 dark:text-white">
            {{ t('pages.workspaces.settings.embed.snippetTitle') }}
          </h3>
          <p class="mt-1 mb-0 text-sm text-muted">
            {{ t('pages.workspaces.settings.embed.snippetDescription') }}
          </p>
        </div>
        <UButton
          icon="i-lucide-copy"
          :label="t('common.actions.copy')"
          :disabled="!embedEnabled || !normalizedSlug"
          @click="copySnippet"
          variant="outline"
        />
      </div>

      <UTextarea
        :model-value="snippet"
        class="w-full font-mono text-sm"
        :rows="8"
        readonly
        autoresize
      />

      <AppAlert v-if="!embedEnabled" color="info" :closable="false" class="mt-3">
        {{ t('pages.workspaces.settings.embed.disabledHint') }}
      </AppAlert>
    </div>
  </div>
</template>
